/**
 * Regelbasierte Vorbelegung der Grundlagen (Plan Stufe 2.1).
 *
 * Liest die Projektdaten, die beim Anlegen des Projekts schon erfasst wurden
 * (Projektbeschreibung, Bauzeit, Verteiler, LV) und macht daraus VORSCHLAEGE
 * fuer die Grundlagen der Planungsphase. Mehr nicht:
 *
 *  - Nichts hier schreibt in die Datenbank. Ein Vorschlag wird je Zeile per
 *    Klick in den ENTWURF der Grundlagen uebernommen (`uebernehmeVorschlaege`),
 *    gespeichert wird weiterhin nur ueber den Speichern-Knopf.
 *  - Es gibt nur JA-Vorschlaege fuer Merkmale, nie Nein. Die Merkmale sind
 *    dreiwertig: ein falsches Ja erzeugt hoechstens ein paar Fragen zu viel,
 *    ein falsches Nein blendet Fragen lautlos aus.
 *  - Vorgeschlagen wird nur, was der Entwurf noch nicht gesetzt hat. Ein
 *    ausdrueckliches Nein des Anwenders ist eine Entscheidung und wird nicht
 *    mit einem Vorschlag ueberredet.
 *  - Jeder Vorschlag nennt seine Quelle und, wo gerechnet wurde, den
 *    Rechenweg — der Anwender muss sehen koennen, woher die Zahl kommt.
 *
 * Pure Funktionen ohne DOM und ohne Supabase; die Anzeige steht in
 * `PlanungVorschlagsLeiste.tsx`.
 */

import type { Contact, ProjectGlobalData, ReportMetadata, ServiceItem } from '../../types';
import { parseDateLocal } from '../../services/dateService';
import { MERKMAL_LABEL } from './fragenkatalog';
import type { ProjektMerkmale } from './types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPEN
// ═══════════════════════════════════════════════════════════════════════════

/** Die booleschen Merkmale — nur sie bekommen Ja-Vorschlaege. */
export type MerkmalSchalter = {
    [K in keyof ProjektMerkmale]-?: ProjektMerkmale[K] extends boolean | undefined ? K : never
}[keyof ProjektMerkmale];

interface VorschlagBasis {
    /** Stabil je Feld (`merkmal:geruest`), damit „Ausblenden" die Zeile wiedererkennt. */
    id: string;
    /** Woher der Vorschlag stammt („Verteiler: Gerüstbau (Schulz GmbH)"). */
    quelle: string;
    /** Wie der Wert zustande kam — Pflicht bei gerechneten Werten. */
    rechenweg?: string;
}

export type PlanungVorschlag =
    | (VorschlagBasis & { art: 'baubeschreibung'; feld: 'baubeschreibung'; wert: string })
    | (VorschlagBasis & { art: 'zahl'; feld: 'arbeitstage'; wert: number })
    | (VorschlagBasis & { art: 'merkmal'; feld: MerkmalSchalter; wert: true })
    /** Reiner Hinweis — setzt kein Feld, laesst sich nur ausblenden. */
    | (VorschlagBasis & { art: 'hinweis'; feld: null; wert: string });

/** Der Teil des Entwurfs der Grundlagen, den die Vorbelegung liest und setzt. */
export interface GrundlagenEntwurf {
    baubeschreibung: string;
    merkmale: ProjektMerkmale;
}

/** Der Ausschnitt der Projektdaten, den die Regeln lesen. */
export type ProjektDatenAuszug = Pick<
    ReportMetadata,
    'projectDescription' | 'distributionList' | 'contractorCompany' | 'globalData'
>;

export interface VorbelegungOptionen {
    /** Name der eigenen Firma (Profil) — steht sie im Verteiler, zaehlt sie
     *  nicht als Arbeitgeber auf der Baustelle. */
    eigeneFirma?: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// HILFEN
// ═══════════════════════════════════════════════════════════════════════════

const text = (v: unknown): string => (v == null ? '' : String(v)).trim();

/** Kleinschreibung ohne Umlaute und Mehrfachleerzeichen — fuer Vergleiche. */
const normalisiere = (s: string): string => s
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/\s+/g, ' ')
    .trim();

/** ISO-Tag → TT.MM.JJJJ durch reines Zerlegen, kein Date-Parsing. */
const tagDe = (iso: string): string => {
    const [j, m, d] = iso.slice(0, 10).split('-');
    return `${d}.${m}.${j}`;
};

/** Date (Ortszeit) → YYYY-MM-DD, ohne `toISOString` (UTC-Tag). */
const isoAus = (d: Date): string => {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const t = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${t}`;
};

const istLeer = (v: unknown): boolean => v === undefined || v === null;

// ═══════════════════════════════════════════════════════════════════════════
// AUSFUEHRUNGSZEITRAUM UND ARBEITSTAGE (Entscheide E1 und E3)
// ═══════════════════════════════════════════════════════════════════════════

export interface Zeitraum {
    /** YYYY-MM-DD */
    von: string;
    bis: string;
    /** Woher die beiden Tage kommen — erscheint im Vorschlag. */
    quelle: string;
}

/**
 * Der Zeitraum, aus dem die Arbeitstage gerechnet werden: der
 * Ausfuehrungszeitraum, sonst die Projektlaufzeit (E1: Rueckfall Projektstart).
 * Fehlt eine Seite, wird sie aus der anderen Quelle genommen und die Quelle
 * sagt das. Fehlt eine Seite ganz oder liegt das Ende vor dem Beginn, gibt es
 * keinen Zeitraum und damit keinen Vorschlag — geraten wird nicht.
 */
export const ausfuehrungszeitraum = (
    g: Pick<ProjectGlobalData, 'executionStartDate' | 'executionEndDate' | 'startDate' | 'endDate'> | null | undefined,
): Zeitraum | null => {
    const gueltig = (v: string | undefined): string | null => (parseDateLocal(v) ? String(v).slice(0, 10) : null);
    const ausfVon = gueltig(g?.executionStartDate);
    const ausfBis = gueltig(g?.executionEndDate);
    const projVon = gueltig(g?.startDate);
    const projBis = gueltig(g?.endDate);

    const von = ausfVon ?? projVon;
    const bis = ausfBis ?? projBis;
    if (!von || !bis || bis < von) return null;

    let quelle: string;
    if (ausfVon && ausfBis) quelle = 'Ausführungszeitraum der Stammdaten';
    else if (!ausfVon && !ausfBis) quelle = 'Projektlaufzeit der Stammdaten (kein Ausführungszeitraum erfasst)';
    else if (ausfVon) quelle = 'Ausführungsbeginn und Projektende der Stammdaten (kein Ausführungsende erfasst)';
    else quelle = 'Projektstart und Ausführungsende der Stammdaten (kein Ausführungsbeginn erfasst)';

    return { von, bis, quelle };
};

export interface Feiertag {
    /** YYYY-MM-DD */
    tag: string;
    name: string;
}

/**
 * Ostersonntag eines Jahres (gregorianisch, Algorithmus nach Gauss in der
 * Fassung von Meeus/Jones/Butcher). Liefert Monat (1–12) und Tag.
 */
export const ostersonntag = (jahr: number): { monat: number; tag: number } => {
    const a = jahr % 19;
    const b = Math.floor(jahr / 100);
    const c = jahr % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const monat = Math.floor((h + l - 7 * m + 114) / 31);
    const tag = ((h + l - 7 * m + 114) % 31) + 1;
    return { monat, tag };
};

/**
 * Die bundeseinheitlichen gesetzlichen Feiertage eines Jahres — nur die neun,
 * die in jedem Land gelten. Landesfeiertage (Fronleichnam, Reformationstag,
 * Allerheiligen, …) haengen vom Bundesland der Baustelle ab, das die
 * Stammdaten nicht strukturiert kennen; sie werden deshalb NICHT abgezogen,
 * und der Rechenweg sagt das.
 */
export const bundesfeiertage = (jahr: number): Feiertag[] => {
    const ostern = ostersonntag(jahr);
    const osternDatum = new Date(jahr, ostern.monat - 1, ostern.tag, 12, 0, 0, 0);
    const abOstern = (tage: number): string => {
        const d = new Date(osternDatum);
        d.setDate(d.getDate() + tage);
        return isoAus(d);
    };
    const fest = (monat: number, tag: number): string =>
        `${jahr}-${String(monat).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
    return [
        { tag: fest(1, 1), name: 'Neujahr' },
        { tag: abOstern(-2), name: 'Karfreitag' },
        { tag: abOstern(1), name: 'Ostermontag' },
        { tag: fest(5, 1), name: 'Tag der Arbeit' },
        { tag: abOstern(39), name: 'Christi Himmelfahrt' },
        { tag: abOstern(50), name: 'Pfingstmontag' },
        { tag: fest(10, 3), name: 'Tag der Deutschen Einheit' },
        { tag: fest(12, 25), name: '1. Weihnachtstag' },
        { tag: fest(12, 26), name: '2. Weihnachtstag' },
    ];
};

export interface ArbeitstageRechnung {
    /** Werktage Montag bis Freitag im Zeitraum, einschliesslich beider Grenzen. */
    werktage: number;
    /** Bundeseinheitliche Feiertage, die auf einen Werktag im Zeitraum fallen. */
    feiertage: Feiertag[];
    /** werktage − feiertage.length */
    arbeitstage: number;
    rechenweg: string;
}

/**
 * Arbeitstage eines Zeitraums: Mo–Fr abzueglich bundeseinheitlicher
 * Feiertage (E3). Gerechnet wird in Ortszeit ueber `parseDateLocal` und
 * `setDate` — nie ueber UTC-Millisekunden, sonst verschiebt die Sommerzeit
 * einen Tag. `null`, wenn die Tage nicht lesbar sind oder das Ende vor dem
 * Beginn liegt.
 */
export const arbeitstageImZeitraum = (vonIso: string, bisIso: string): ArbeitstageRechnung | null => {
    const von = parseDateLocal(vonIso);
    const bis = parseDateLocal(bisIso);
    if (!von || !bis || bis < von) return null;

    const feiertagsnamen = new Map<string, string>();
    for (let j = von.getFullYear(); j <= bis.getFullYear(); j++) {
        bundesfeiertage(j).forEach(f => feiertagsnamen.set(f.tag, f.name));
    }

    let werktage = 0;
    const feiertage: Feiertag[] = [];
    const lauf = new Date(von);
    while (lauf <= bis) {
        const wochentag = lauf.getDay();
        if (wochentag >= 1 && wochentag <= 5) {
            werktage++;
            const iso = isoAus(lauf);
            const name = feiertagsnamen.get(iso);
            if (name) feiertage.push({ tag: iso, name });
        }
        lauf.setDate(lauf.getDate() + 1);
    }

    const arbeitstage = werktage - feiertage.length;
    const zeitraumText = `${tagDe(vonIso)} bis ${tagDe(bisIso)}`;
    const abzug = feiertage.length
        ? `, abzüglich ${feiertage.length} bundeseinheitliche${feiertage.length === 1 ? 'r Feiertag' : ' Feiertage'} `
            + `(${feiertage.map(f => `${f.name} ${tagDe(f.tag)}`).join(', ')}) = ${arbeitstage} Arbeitstage`
        : ', kein bundeseinheitlicher Feiertag darin';
    const rechenweg = `${zeitraumText}: ${werktage} Werktage Mo bis Fr${abzug}. `
        + 'Nicht berücksichtigt: Landesfeiertage, Betriebsferien, Schlechtwetter, Schichtbetrieb.';

    return { werktage, feiertage, arbeitstage, rechenweg };
};

// ═══════════════════════════════════════════════════════════════════════════
// VERTEILER → ARBEITGEBER UND MERKMALE
// ═══════════════════════════════════════════════════════════════════════════

/** Rollen, die keinen Arbeitgeber AUF der Baustelle bezeichnen (Teilwort reicht). */
const NICHT_AUSFUEHREND = [
    'bauherr', 'auftraggeber', 'sigeko', 'koordinator', 'architekt', 'planer', 'statiker',
    'behoerde', 'gutachter', 'sachverstaendig', 'versorger',
];

/** Rollen, die ein ausfuehrendes Unternehmen bezeichnen — Teilwort („Nachunternehmer") … */
const AUSFUEHREND = ['gewerk', 'unternehm', 'firma'];
/** … oder genau dieses Wort („GU" allein; als Teilwort traefe es zu viel). */
const AUSFUEHREND_GENAU = ['gu', 'an', 'nu'];

const trifftRolle = (rolle: string, teilwoerter: string[], genau: string[] = []): boolean =>
    teilwoerter.some(r => rolle.includes(r)) || genau.includes(rolle);

/**
 * Zaehlt der Kontakt als ausfuehrende Firma? Bauherr, Planer, Behoerden und
 * die eigene Firma nicht; „Gewerk" und erkennbar Unternehmer schon; ein
 * Kontakt mit eingetragenem Gewerk (ausser „Bauleitung / SiGeKo") ebenfalls.
 * Eine Bauleitung OHNE Gewerk ist meist die Objektueberwachung des Bauherrn
 * und zaehlt deshalb nicht.
 */
export const istAusfuehrendeFirma = (
    k: Pick<Contact, 'role' | 'tradeType' | 'company'>,
    eigeneFirma?: string | null,
): boolean => {
    const firma = normalisiere(text(k.company));
    if (!firma) return false;
    if (eigeneFirma && normalisiere(eigeneFirma) === firma) return false;

    const rolle = normalisiere(text(k.role));
    const gewerk = normalisiere(text(k.tradeType));
    if (gewerk.includes('sigeko') || gewerk.includes('koordinator')) return false;
    if (trifftRolle(rolle, NICHT_AUSFUEHREND)) return false;
    if (trifftRolle(rolle, AUSFUEHREND, AUSFUEHREND_GENAU)) return true;
    return gewerk.length > 0;
};

/**
 * Die ausfuehrenden Firmen des Projekts, je Firma einmal (Schreibweise des
 * ersten Treffers). Der in den Stammdaten eingetragene beauftragte
 * Unternehmer (`contractorCompany`) zaehlt mit — er steht oft nicht noch
 * einmal im Verteiler.
 */
export const ausfuehrendeFirmen = (
    daten: Pick<ProjektDatenAuszug, 'distributionList' | 'contractorCompany'> | null | undefined,
    eigeneFirma?: string | null,
): string[] => {
    const gesehen = new Map<string, string>();
    const merke = (name: string) => {
        const schluessel = normalisiere(name);
        if (schluessel && !gesehen.has(schluessel)) gesehen.set(schluessel, name.trim());
    };
    const unternehmer = text(daten?.contractorCompany);
    if (unternehmer && !(eigeneFirma && normalisiere(eigeneFirma) === normalisiere(unternehmer))) merke(unternehmer);
    (daten?.distributionList || []).forEach(k => {
        if (k && istAusfuehrendeFirma(k, eigeneFirma)) merke(text(k.company));
    });
    return Array.from(gesehen.values());
};

/**
 * Gewerk (`Contact.tradeType`, Liste in ContactsView) → Merkmale. Geprueft
 * wird auf normalisierte Schluesselwoerter, damit auch frei eingetippte
 * Gewerke („Elektro Krause", „Dachdeckerei") treffen. Unbekannte Gewerke
 * loesen nichts aus. Nur Ja-Merkmale.
 */
const GEWERK_REGELN: { schluessel: string[]; merkmale: MerkmalSchalter[] }[] = [
    { schluessel: ['asbest', 'schadstoffsanierung'], merkmale: ['bestand', 'abbruch'] },
    { schluessel: ['abbruch', 'demontage', 'rueckbau', 'entkernung'], merkmale: ['abbruch'] },
    { schluessel: ['geruest'], merkmale: ['geruest'] },
    { schluessel: ['kran'], merkmale: ['kran'] },
    { schluessel: ['tiefbau', 'erdbau', 'erdarbeiten', 'kampfmittel', 'kanalbau', 'leitungsbau'], merkmale: ['erdarbeiten'] },
    { schluessel: ['dachdeck', 'dacharbeiten', 'dachabdichtung', 'dach'], merkmale: ['dacharbeiten'] },
    { schluessel: ['strassenbau', 'asphalt', 'verkehrssicherung'], merkmale: ['strassenbaustelle'] },
    { schluessel: ['heizung', 'sanitaer', 'hls', 'elektro', 'kaelte', 'klima', 'lueftung', 'tga'], merkmale: ['tga'] },
    { schluessel: ['stahlbau', 'fertigteil', 'betonfertigteil', 'holzbau', 'zimmerer'], merkmale: ['fertigteilmontage'] },
    // „betonbau" statt „beton": das Teilwort traefe auch „Betonfertigteile".
    { schluessel: ['rohbau', 'stahlbeton', 'betonbau', 'ortbeton', 'schalung'], merkmale: ['ortbeton'] },
];

/** Welche Merkmale ein einzelnes Gewerk nahelegt (leer bei unbekanntem). */
export const merkmaleAusGewerk = (gewerk: string | null | undefined): MerkmalSchalter[] => {
    const g = normalisiere(text(gewerk));
    if (!g) return [];
    const treffer = new Set<MerkmalSchalter>();
    GEWERK_REGELN.forEach(regel => {
        if (regel.schluessel.some(s => g.includes(s))) regel.merkmale.forEach(m => treffer.add(m));
    });
    return Array.from(treffer);
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE VORSCHLAEGE
// ═══════════════════════════════════════════════════════════════════════════

/** A+S-Positionen im LV — beauftragte, keine Eventualpositionen (NEP). */
const asplanPositionen = (services: ServiceItem[] | undefined): ServiceItem[] =>
    (services || []).filter(s => s && s.assignment === 'asplan' && s.is_optional !== true);

/**
 * Alle Vorschlaege aus den Projektdaten fuer den gegebenen Entwurf. Ein Feld,
 * das im Entwurf schon gesetzt ist (Text nicht leer, Merkmal nicht
 * `undefined`), bekommt keinen Vorschlag — auch nicht bei `false`.
 * Reihenfolge wie die Maske: Baubeschreibung, Umfang, Merkmale, Hinweise.
 */
export const vorschlaegeAusProjekt = (
    daten: ProjektDatenAuszug | null | undefined,
    entwurf: GrundlagenEntwurf,
    optionen: VorbelegungOptionen = {},
): PlanungVorschlag[] => {
    if (!daten) return [];
    const merkmale = entwurf.merkmale || {};
    const aus: PlanungVorschlag[] = [];

    // ─── Baubeschreibung ← Projektbeschreibung ──────────────────────────
    const beschreibung = text(daten.projectDescription);
    if (beschreibung && !text(entwurf.baubeschreibung)) {
        aus.push({
            id: 'baubeschreibung',
            art: 'baubeschreibung',
            feld: 'baubeschreibung',
            wert: beschreibung,
            quelle: 'Projektbeschreibung der Stammdaten',
            rechenweg: 'Wortgleich übernommen, in der Planungsphase frei weiterzuschreiben.',
        });
    }

    // ─── Arbeitstage ← Ausfuehrungszeitraum (E1, E3) ─────────────────────
    if (istLeer(merkmale.arbeitstage)) {
        const zeitraum = ausfuehrungszeitraum(daten.globalData);
        const rechnung = zeitraum ? arbeitstageImZeitraum(zeitraum.von, zeitraum.bis) : null;
        if (zeitraum && rechnung && rechnung.arbeitstage > 0) {
            aus.push({
                id: 'arbeitstage',
                art: 'zahl',
                feld: 'arbeitstage',
                wert: rechnung.arbeitstage,
                quelle: zeitraum.quelle,
                rechenweg: rechnung.rechenweg,
            });
        }
    }

    // ─── Mehrere Arbeitgeber ← Firmen im Verteiler ───────────────────────
    if (istLeer(merkmale.mehrereArbeitgeber)) {
        const firmen = ausfuehrendeFirmen(daten, optionen.eigeneFirma);
        if (firmen.length > 1) {
            aus.push({
                id: 'merkmal:mehrereArbeitgeber',
                art: 'merkmal',
                feld: 'mehrereArbeitgeber',
                wert: true,
                quelle: `Verteiler: ${firmen.length} ausführende Firmen`,
                rechenweg: `${firmen.join(', ')}. Bauherr, Planer, Behörden und die eigene Firma nicht gezählt; `
                    + 'Nachunternehmer, die noch nicht im Verteiler stehen, kommen hinzu.',
            });
        }
    }

    // ─── Merkmale ← Gewerke der Kontakte ─────────────────────────────────
    const belege = new Map<MerkmalSchalter, string[]>();
    (daten.distributionList || []).forEach(k => {
        const gewerk = text(k?.tradeType);
        if (!gewerk) return;
        const beleg = text(k.company) ? `${gewerk} (${text(k.company)})` : gewerk;
        merkmaleAusGewerk(gewerk).forEach(m => {
            const liste = belege.get(m) || [];
            if (!liste.includes(beleg)) liste.push(beleg);
            belege.set(m, liste);
        });
    });
    // Reihenfolge der Regeln, nicht der Kontakte — die Leiste bleibt so stabil.
    GEWERK_REGELN.flatMap(r => r.merkmale).forEach(m => {
        const liste = belege.get(m);
        if (!liste || !istLeer(merkmale[m])) return;
        if (aus.some(v => v.id === `merkmal:${m}`)) return;
        aus.push({
            id: `merkmal:${m}`,
            art: 'merkmal',
            feld: m,
            wert: true,
            quelle: `Verteiler: ${liste.join(', ')}`,
            rechenweg: `Gewerk im Verteiler legt „${MERKMAL_LABEL[m] || m}“ nahe. Nur Ja wird vorgeschlagen. `
                + 'Ein Nein blendet Fragen aus und bleibt Ihre Entscheidung.',
        });
    });

    // ─── Hinweis: A+S-Plan im LV ─────────────────────────────────────────
    const asplan = asplanPositionen(daten.globalData?.services);
    if (asplan.length) {
        const positionen = asplan
            .map(s => [text(s.lvPosition), text(s.title) ? `„${text(s.title)}“` : ''].filter(Boolean).join(' '))
            .filter(Boolean);
        aus.push({
            id: 'hinweis:asplan',
            art: 'hinweis',
            feld: null,
            wert: 'A+S-Plan beauftragt: Schadstoffmerkmale klären (Bestand, Baujahr, Abbruch, Stoffe nach Anhang II Nr. 2).',
            quelle: `LV: ${positionen.length ? positionen.join('; ') : `${asplan.length} Position(en)`} mit Zuordnung A+S-Plan`,
            rechenweg: 'Setzt kein Merkmal: Ein beauftragter A+S-Plan belegt den Verdacht, nicht den Befund.',
        });
    }

    return aus;
};

/**
 * Uebernimmt Vorschlaege in den Entwurf — dieselbe Wirkung wie eine Eingabe
 * in der Maske, nicht mehr. Hinweise aendern nichts. Liefert den Entwurf
 * unveraendert zurueck, wenn nichts zu tun ist (Objektidentitaet bleibt).
 */
export const uebernehmeVorschlaege = <E extends GrundlagenEntwurf>(entwurf: E, liste: readonly PlanungVorschlag[]): E => {
    let neu: E | null = null;
    const arbeitskopie = (): E => {
        if (!neu) neu = { ...entwurf, merkmale: { ...(entwurf.merkmale || {}) } };
        return neu;
    };
    liste.forEach(v => {
        switch (v.art) {
            case 'baubeschreibung':
                arbeitskopie().baubeschreibung = v.wert;
                break;
            case 'zahl':
                arbeitskopie().merkmale[v.feld] = v.wert;
                break;
            case 'merkmal':
                arbeitskopie().merkmale[v.feld] = true;
                break;
            case 'hinweis':
                break;
        }
    });
    return neu ?? entwurf;
};

/** Lesbarer Name des Zielfelds fuer die Leiste. */
export const vorschlagFeldLabel = (v: PlanungVorschlag): string => {
    switch (v.art) {
        case 'baubeschreibung': return 'Baubeschreibung';
        case 'zahl': return 'Bauzeit (Arbeitstage)';
        case 'merkmal': return v.feld === 'mehrereArbeitgeber'
            ? 'Beschäftigte mehrerer Arbeitgeber'
            : (MERKMAL_LABEL[v.feld] || v.feld);
        case 'hinweis': return 'Hinweis';
    }
};

/** Anzeigewert einer Zeile („Ja", „82", gekuerzter Text). */
export const vorschlagWertText = (v: PlanungVorschlag, maxZeichen = 160): string => {
    switch (v.art) {
        case 'merkmal': return 'Ja';
        case 'zahl': return String(v.wert);
        case 'baubeschreibung':
        case 'hinweis': {
            const t = v.wert.replace(/\s+/g, ' ').trim();
            return t.length > maxZeichen ? `${t.slice(0, maxZeichen - 1).trimEnd()}…` : t;
        }
    }
};

/** Vorschlaege, die ein Feld setzen — „Alle übernehmen" nimmt nur diese. */
export const uebernehmbareVorschlaege = (liste: readonly PlanungVorschlag[]): PlanungVorschlag[] =>
    liste.filter(v => v.art !== 'hinweis');
